import { observer } from 'mobx-react-lite'
import {
  AiArtworkServiceStatus,
  AiCollectionVisibleType,
  getCompetitionProductDetail_itemModule_artwork,
  getPostCollectionForCompetition,
  getPostCollectionForCompetitionVariables,
  getPostCollectionForCompetition_squareModule_squarePost_aiCollection_aiArtworks_edges_node,
} from '~/graphqls/types'

import styles from './AdvancedInfoForImage.module.css'
import classNames from 'classnames'
import { useMemo, useState } from 'react'
import { MyLink } from '~/components/ui/MyLink'
import { MyIcon } from '~/components/ui/MyIcon'
import { getGraphDataWithCursor, toCDNImage } from '~/utils'
import useInfiniteScroll from '~/hooks/useInfiniteScroll'
import { GET_POST_COLLECTION_FOR_COMPETITION } from '~/graphqls/gqls'
import { MyImage } from '~/components/ui/MyImage'
import Link from 'next/link'

interface Props {
  product: getCompetitionProductDetail_itemModule_artwork
  isSelf?: boolean
  needLinkAiCollection?: boolean | null
}

function AdvancedInfoForVideo({
  product,
  isSelf = false,
  needLinkAiCollection = false,
}: Props) {
  const collectionPost = product.contestInfoExtra?.collectionPost
  const [currentInfoIndex, setCurrentInfoIndex] = useState(
    needLinkAiCollection ? 0 : 1
  )
  const [initData] = useState(() => {
    return getGraphDataWithCursor(
      collectionPost?.aiCollection?.aiArtworks?.edges || []
    )
  })

  const { InfiniteScollComp, list } = useInfiniteScroll<
    getPostCollectionForCompetition_squareModule_squarePost_aiCollection_aiArtworks_edges_node,
    getPostCollectionForCompetition,
    getPostCollectionForCompetitionVariables
  >({
    showLoadingText: false,
    queryDoc: GET_POST_COLLECTION_FOR_COMPETITION,
    extraQueryParams: {
      id: collectionPost?.id,
    },
    initData: initData,
    hasNextPage:
      collectionPost?.aiCollection?.aiArtworks?.pageInfo?.hasNextPage || false,
    hasNextPageExtractor: res => {
      return (
        res.data?.squareModule?.squarePost?.aiCollection?.aiArtworks?.pageInfo
          ?.hasNextPage || false
      )
    },
    dataExtractor: res => {
      return getGraphDataWithCursor(
        res.data?.squareModule?.squarePost?.aiCollection?.aiArtworks?.edges ||
          []
      )
    },
  })

  const createInfoItems = [
    {
      name: '合集名称',
      value: collectionPost?.title,
    },
  ]

  const techInfoItems = [
    {
      icon: 'hash',
      name: '链上Hash',
      value: product?.chain?.hash || '上链中',
    },
    {
      icon: 'bank',
      name: '作品存证',
      value: (
        <MyLink
          href={`/evidence?id=${product?.chain?.certificateNo}`}
          exist={false}
        >
          <div className="flex items-center justify-end">
            <span>查看</span>
            <MyIcon icon="arrow-right-simple text-[24px]" />
          </div>
        </MyLink>
      ),
    },
  ]

  let collectionLink = `/collection/private/${collectionPost?.aiCollection?.id}`
  if (
    collectionPost?.aiCollection?.visible === AiCollectionVisibleType.SQUARE
  ) {
    collectionLink = `/collection/${collectionPost?.id}`
  }

  const renderList = useMemo(() => {
    if (
      isSelf ||
      collectionPost?.aiCollection?.visible === AiCollectionVisibleType.SQUARE
    )
      return list
    return list.filter(
      it => it.serviceStatus === AiArtworkServiceStatus.GENERAL
    )
  }, [list])

  return (
    <>
      {needLinkAiCollection && (
        <div className={styles.tabs} style={{ marginBottom: 0 }}>
          <div
            className={classNames(styles.tabItem, {
              active: currentInfoIndex === 0,
            })}
            onClick={() => setCurrentInfoIndex(0)}
          >
            合集信息
          </div>
          <div
            className={classNames(styles.tabItem, {
              active: currentInfoIndex === 1,
            })}
            onClick={() => setCurrentInfoIndex(1)}
          >
            技术信息
          </div>
        </div>
      )}
      <div className="mt-6">
        <div>
          {currentInfoIndex === 0 && (
            <>
              <div className="flex items-center text-neutral-500 text-sm mb-3 justify-between">
                <div>查看合集</div>
                <Link href={collectionLink}>
                  <div className="flex items-center justify-end text-primary text-xs">
                    <span>查看全部</span>
                    <MyIcon icon="arrow-right text-[24px]" />
                  </div>
                </Link>
              </div>

              {!!renderList.length && (
                <div
                  className="flex items-center overflow-x-auto mb-6 rounded-lg scrollbar-hidden"
                  style={{
                    width: `336px`,
                  }}
                >
                  <div className="flex items-center gap-2">
                    {renderList.map(item => {
                      return (
                        <div key={item.id}>
                          {item.serviceStatus ===
                          AiArtworkServiceStatus.CLEANED ? (
                            <div
                              className="rounded-lg bg-neutral-200 flex flex-col items-center justify-center w-[96px] h-[128px]"
                              style={{
                                background: `url(${toCDNImage(
                                  '/images/ai/clear-bg@2x.png'
                                )})`,
                                backgroundSize: 'cover',
                              }}
                            >
                              <MyIcon
                                icon="yasuo"
                                className="text-neutral-500 text-[24px]"
                              />
                              <div className="text-neutral-400 text-xs mt-2 text-center">
                                已压缩
                              </div>
                            </div>
                          ) : (
                            <MyImage
                              src={item.miniArtWorkUrl || ''}
                              alt=""
                              className="w-[96px] h-[128px] block rounded-lg"
                            />
                          )}
                        </div>
                      )
                    })}

                    {InfiniteScollComp}
                    {/* {loading && (
                <div className="w-4">
                  <Spin size="small"></Spin>
                </div>
              )} */}
                  </div>
                </div>
              )}
            </>
          )}
          {currentInfoIndex === 0 && (
            <div className={styles.infos}>
              {createInfoItems.map(it => (
                <div key={it.name}>
                  <span>{it.name}</span>
                  <span>{it.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        {currentInfoIndex === 1 && (
          // 最小高度和创作信息栏对齐
          <div className={classNames(styles.infos, 'min-h-[212px]')}>
            {techInfoItems.map(it => (
              <div key={it.name}>
                <span>{it.name}</span>
                <span>{it.value}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

export default observer(AdvancedInfoForVideo)
